// /owner/program/:id/builder — hosts the 5-step ProgramWizard (slice 4a-3;
// program-id-scoped since SLICE-PASS-TYPES).
//
// Guards on the program existing: a direct hit with no matching program (or a
// loaded-but-null program) bounces to /owner/program, which lists the tenant's
// programs + the create CTA. An active program enters the wizard in
// cosmetic-only mode (steps 3+4).

import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { ProgramWizard } from '../program/ProgramWizard.js'
import { useProgram } from '../program/hooks/useProgram.js'
import { useOwnerSession } from '../hooks/useOwnerSession.js'

export function ProgramBuilderPage() {
  const navigate = useNavigate()
  const { id = '' } = useParams<{ id: string }>()
  const { data: program, isLoading, isError } = useProgram(id)
  const { data: me } = useOwnerSession()
  // Staging-only escape hatch: when the API has ALLOW_STRUCTURAL_EDITS=1,
  // /me returns dev_unlock_structural_edits=true. We drop the cosmeticOnly
  // gate so the tester can iterate on a single tenant. Always false in prod.
  const devUnlock = me?.dev_unlock_structural_edits === true

  // No program to build → send them to the landing page's create CTA.
  useEffect(() => {
    if (!isLoading && !isError && program === null) {
      navigate('/owner/program', { replace: true })
    }
  }, [isLoading, isError, program, navigate])

  if (isLoading) {
    return <p className="owner-loading">Loading…</p>
  }
  if (isError) {
    return (
      <p className="owner-loading">
        Couldn't load your program. Go back to{' '}
        <a href="/owner/program">Loyalty program</a> and try again.
      </p>
    )
  }
  if (!program) {
    // The redirect effect is in flight — render nothing rather than flash.
    return null
  }

  const isMembership = program.pass_type === 'membership'

  return (
    <div>
      <h1>
        {program.status === 'active'
          ? 'Edit your pass'
          : isMembership
            ? 'Build your membership'
            : 'Build your loyalty program'}
      </h1>
      <p className="owner-greeting-sub">
        {program.status === 'active'
          ? isMembership
            ? 'Update colors, the tier, benefits, links, and terms.'
            : 'Update colors, the stamp icon, links, and terms.'
          : 'A few quick steps. Your changes save as you go.'}
      </p>
      {/* key by program id so switching between programs (same route, changing
          :id) fully remounts the wizard — resets stepIndex + builder draft
          instead of bleeding state from the previously-edited program. */}
      <ProgramWizard
        key={program.id}
        program={program}
        cosmeticOnly={program.status === 'active' && !devUnlock}
        devUnlock={devUnlock}
      />
    </div>
  )
}
